'use client';

import { LinkList, LinkListItem } from '@petsmart-ui/sparky';
import cn from 'classnames';

import { dataLayerPush, getBaseUrlJoined, layoutEvents, trackMainNavLinkClick } from '@libs/analytics';
import { BREAKPOINTS, useDeviceType, useWindowSize } from '@libs/common-next';

import type { Link, LinkAction } from '../../types';
import { getLinkFromLinkOrLinkAction } from '../../utils/getLinkFromLinkOrLinkAction';
import { propsMapper } from '../../utils/propsMapper';
import type { SecondaryNavigationProps } from './types';
import './SecondaryNavigation.scss';

export const trackingData = (data: object) => Object.assign(data, { ...layoutEvents.headerClick });

export default function SecondaryNavigation({
    navigationLinks,
    tabletNavigationLinks,
    isHeader = true,
    className,
}: SecondaryNavigationProps) {
    const size = useWindowSize();

    const classes = cn(isHeader ? 'link-list' : 'link-list link-list__drawer', className);
    const linkBreakpointClass = size > BREAKPOINTS.lg ? 'sparky-u-margin-left-md' : 'sparky-u-margin-left-sm';
    const linkClasses = cn({
        [`link-list--normal ${linkBreakpointClass}`]: isHeader,
        'link-list--big sparky-u-margin-left-md': !isHeader,
    });
    const { isDesktop } = useDeviceType();
    const links = propsMapper<LinkAction | Link>((isDesktop ? navigationLinks : tabletNavigationLinks) ?? []);

    const flatLinks = links.map(linkOrLinkAction => getLinkFromLinkOrLinkAction(linkOrLinkAction));

    return (
        <LinkList
            data-testid="secondary-navigation"
            className={classes}
            size={isHeader ? 'sm' : undefined}
            behavior={isHeader ? 'horizontal' : undefined}
        >
            {flatLinks.map(({ actionText, webUrl, iconName }) => (
                <LinkListItem
                    //  @ts-expect-error expects social icons and regular icons, while receives only regular icons
                    iconName={iconName}
                    iconPosition="after"
                    className={linkClasses}
                    onClick={() => {
                        trackMainNavLinkClick(actionText);

                        dataLayerPush(
                            trackingData({
                                selection: actionText,
                                link_url: getBaseUrlJoined(webUrl),
                            })
                        );
                    }}
                    key={webUrl}
                    href={webUrl}
                    text={actionText}
                />
            ))}
        </LinkList>
    );
}
